'use client';
import { useCallback, useState } from 'react';

import { getNSFWRoutesRedirect } from '~constants/routes';
import { ConfirmModal, NSFWAgeConfirmation } from '~features/nsfw';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { usePathname, useRouter } from '~i18n/navigation';
import { GTMAnalytics } from '~services/analytics';

import { AgeConfirmationContext } from './index';
import type { TAgeConfirmationConfig } from './types';

type Props = {
	children: React.ReactNode;
};

export const AgeConfirmationProvider = ({ children }: Props) => {
	const { isOpen, open, close } = useModal();
	const [showCancelButton, setShowCancelButton] = useState(true);
	const { isDemoUser } = useSession();
	const t = useTranslations('AgeConfirmationModal');

	const pathname = usePathname();
	const router = useRouter();

	const openAgeConfirmation = useCallback(
		(config: TAgeConfirmationConfig) => {
			open({ cb: config.callback })();
		},
		[open]
	);

	const handleConfirm = useCallback(() => {
		setShowCancelButton(true);
		close(true);
		if (isDemoUser) {
			GTMAnalytics.ageConfirmed();
		}
	}, [close, isDemoUser]);

	const handleCancel = useCallback(() => {
		if (getNSFWRoutesRedirect(pathname)) {
			router.push(getNSFWRoutesRedirect(pathname));
		}
		setShowCancelButton(true);
		close(false);
	}, [close, pathname, router]);

	return (
		<AgeConfirmationContext.Provider value={{ openAgeConfirmation }}>
			{children}

			<NSFWAgeConfirmation />

			<ConfirmModal
				title={t('modalTitle')}
				info={t('modalText')}
				locked={true}
				hasCancel={showCancelButton}
				onConfirm={handleConfirm}
				onClose={close}
				onCancel={handleCancel}
				open={isOpen}
				confirmText={t('confirmText')}
				cancelText={t('cancelText')}
				align="left"
				transparent={false}
				multiline={true}
				hasClose={false}
			/>
		</AgeConfirmationContext.Provider>
	);
};
